import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AvatarSourceEditor from "@/components/avatars/AvatarSourceEditor";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  clearSocialAvatar,
  clearSocialBanner,
  setSocialAvatarUrl,
  setSocialBannerUrl,
  uploadSocialAvatar,
  uploadSocialBanner,
} from "@/lib/avatarApi";
import { toastError, toastSuccess } from "@/lib/toast";
import { useSocialProfile } from "../scripts/SocialProfileContext";

/**
 * Self-only edit profile modal: display name, bio, PFP, cover.
 */
const SocialEditProfileDialog = ({
  open,
  onOpenChange,
  accessToken,
  profile,
  onSaved,
}) => {
  const { refresh } = useSocialProfile();
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [localProfile, setLocalProfile] = useState(profile);

  useEffect(() => {
    if (!open || !profile) return;
    setDisplayName(profile.display_name || "");
    setBio(profile.bio || "");
    setLocalProfile(profile);
  }, [open, profile]);

  const applyMediaResult = (data) => {
    const next = data?.profile || data;
    if (next) {
      setLocalProfile(next);
      onSaved?.(next);
    }
  };

  const handleSaveText = async () => {
    if (!accessToken || saving) return;
    const name = displayName.trim();
    if (!name) {
      toastError("Display name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/social/me/profile`,
        {
          display_name: name,
          bio: bio.trim(),
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const next = res.data?.profile;
      setLocalProfile(next);
      await refresh();
      onSaved?.(next);
      toastSuccess("Profile updated");
      onOpenChange(false);
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not save profile"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit profile</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <AvatarSourceEditor
            label="Cover photo"
            currentUrl={localProfile?.banner_url}
            crop
            previewShape="rect"
            onSaveUrl={async (url) => {
              const data = await setSocialBannerUrl(accessToken, url);
              applyMediaResult(data);
              await refresh();
              toastSuccess("Cover updated");
            }}
            onUploadFile={async (file) => {
              const data = await uploadSocialBanner(accessToken, file);
              applyMediaResult(data);
              await refresh();
              toastSuccess("Cover updated");
            }}
            onClear={async () => {
              const data = await clearSocialBanner(accessToken);
              applyMediaResult(data);
              await refresh();
              toastSuccess("Cover removed");
            }}
            disabled={saving}
          />

          <AvatarSourceEditor
            label="Profile photo"
            currentUrl={localProfile?.avatar_url}
            onSaveUrl={async (url) => {
              const data = await setSocialAvatarUrl(accessToken, url);
              applyMediaResult(data);
              await refresh();
              toastSuccess("Photo updated");
            }}
            onUploadFile={async (file) => {
              const data = await uploadSocialAvatar(accessToken, file);
              applyMediaResult(data);
              await refresh();
              toastSuccess("Photo updated");
            }}
            onClear={async () => {
              const data = await clearSocialAvatar(accessToken);
              applyMediaResult(data);
              await refresh();
              toastSuccess("Photo removed");
            }}
            disabled={saving}
          />

          <div className="space-y-2">
            <Label htmlFor="social-edit-display-name">Display name</Label>
            <Input
              id="social-edit-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              disabled={saving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="social-edit-bio">Bio</Label>
            {/* native textarea — no ui/textarea primitive in this app */}
            <textarea
              id="social-edit-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={500}
              rows={4}
              disabled={saving}
              placeholder="Tell people a bit about yourself"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground text-right">
              {bio.length}/500
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            Handle @{localProfile?.username || "…"}
            {localProfile?.username_can_change === false &&
            localProfile?.username_next_change_at ? (
              <>
                {" "}
                can be renamed again after{" "}
                {new Date(
                  localProfile.username_next_change_at
                ).toLocaleDateString()}
                . Manage renames in{" "}
                <Link
                  to="/profile?tab=account"
                  className="underline underline-offset-2"
                  onClick={() => onOpenChange(false)}
                >
                  Circa account
                </Link>
                .
              </>
            ) : (
              <>
                {" "}
                — rename (once every 6 months) in{" "}
                <Link
                  to="/profile?tab=account"
                  className="underline underline-offset-2"
                  onClick={() => onOpenChange(false)}
                >
                  Circa account
                </Link>
                .
              </>
            )}
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSaveText} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SocialEditProfileDialog;
